import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_CLIENT, PATH_CLIENTS, PATH_ROOT } from "@/constants";
import { ApiError } from "@/services/api";
import { ReadClient, WriteClient } from "@/types/client";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import ClientForm from "./ClientForm";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { ClientService } from "../service";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function ClientCreator() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.Clients);
  const { can_create } = useModulePermissions();
  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("clients.title.multiple"), href: PATH_CLIENTS },
      { label: t("clients.new") },
    ],
    [t],
  );

  const clientMutation = useMutation<ReadClient, ApiError, WriteClient>({
    mutationFn: ClientService.post,
    onSuccess: (response: ReadClient) => {
      const id = response.id;
      navigate(
        generatePath(PATH_CLIENT, {
          clientId: id,
        }),
      );
      toast({
        title: t("clients.success.create"),
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("clients.errors.createClient"),
        description,
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ClientForm
        onSubmit={clientMutation.mutateAsync}
        disabled={clientMutation.isPending || !can_create}
      />
    </>
  );
}
